import { useState, useEffect } from 'react';
import { getPendingAlerts, sendAllAlerts, dismissAlert, checkAlerts } from '../services/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Send, SendHorizonal, X, RefreshCw, Zap, Flame, AlertTriangle, CheckCircle2, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import EmailPreviewModal from '../components/EmailPreviewModal';

const PendingAlertsPage = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [previewLogId, setPreviewLogId] = useState(null);

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    try {
      const res = await getPendingAlerts();
      setPending(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    try {
      const res = await checkAlerts();
      const { triggered } = res.data;
      if (triggered > 0) {
        toast.success(`${triggered} nouveau(x) seuil(s) franchi(s)`);
      } else {
        toast.info('Aucun nouveau seuil franchi');
      }
      fetchPending();
    } catch (error) {
      toast.error('Erreur lors de la verification');
    } finally {
      setChecking(false);
    }
  };

  const handleDismiss = async (logId) => {
    try {
      await dismissAlert(logId);
      toast.info('Alerte ignoree');
      setPending(prev => prev.filter(p => p.id !== logId));
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleSendAll = async () => {
    if (!window.confirm(`Envoyer ${pending.length} alerte(s) par e-mail avec les textes par defaut ?`)) return;
    setSendingAll(true);
    try {
      const res = await sendAllAlerts();
      toast.success(res.data.message);
      setPending([]);
    } catch (error) {
      toast.error("Erreur lors de l'envoi groupe");
    } finally {
      setSendingAll(false);
    }
  };

  const handleSent = (logId) => {
    setPending(prev => prev.filter(p => p.id !== logId));
  };

  // Group by client
  const grouped = {};
  pending.forEach(p => {
    const key = p.client_id;
    if (!grouped[key]) grouped[key] = { client_name: p.client_name || 'Client', alerts: [] };
    grouped[key].alerts.push(p);
  });
  const groupedList = Object.values(grouped).sort((a, b) => a.client_name.localeCompare(b.client_name));

  if (loading) {
    return (
      <div className="space-y-4" data-testid="pending-alerts-loading">
        <Skeleton className="h-10 w-64 bg-white" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 bg-white" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pending-alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E]">Alertes à envoyer</h1>
          <p className="text-sm text-[#687175] mt-1">
            {pending.length} alerte(s) en attente d'envoi
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCheck} disabled={checking} className="btn-secondary" data-testid="check-thresholds-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} /> Vérifier les seuils
          </Button>
          {pending.length > 0 && (
            <Button onClick={handleSendAll} disabled={sendingAll} className="btn-primary" data-testid="send-all-btn">
              <SendHorizonal className={`w-4 h-4 mr-2 ${sendingAll ? 'animate-pulse' : ''}`} />
              {sendingAll ? 'Envoi en cours...' : `Tout envoyer (${pending.length})`}
            </Button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {pending.length === 0 && (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-12 text-center">
          <CheckCircle2 className="w-12 h-12 text-[#10D183] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#191D1E] mb-2">Aucune alerte en attente</h3>
          <p className="text-sm text-[#687175]">
            Toutes les alertes ont été traitées. Cliquez sur "Vérifier les seuils" pour détecter de nouveaux franchissements.
          </p>
        </div>
      )}

      {/* Grouped alerts */}
      <AnimatePresence>
        {groupedList.map(group => (
          <motion.div
            key={group.client_name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-white border border-[#BFC6CC]/12 rounded-2xl overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-[#BFC6CC]/12 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#397DFF]/10 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-[#397DFF]" />
                </div>
                <div>
                  <p className="font-bold text-[#191D1E]">{group.client_name}</p>
                  <p className="text-xs text-[#687175]">{group.alerts.length} alerte(s)</p>
                </div>
              </div>
            </div>

            <div className="divide-y divide-[#BFC6CC]/10">
              {group.alerts.map(alert => (
                <div key={alert.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`pending-alert-${alert.id}`}>
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      alert.energy_type === 'gas' ? 'bg-[#397DFF]/10' : 'bg-[#F9C80E]/10'
                    }`}>
                      {alert.energy_type === 'gas' ? (
                        <Flame className="w-4 h-4 text-[#397DFF]" />
                      ) : (
                        <Zap className="w-4 h-4 text-[#F9C80E]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#191D1E]">{alert.contract_name}</p>
                      <p className="text-xs text-[#687175]">
                        {alert.trigger_type === 'max' ? 'Prix haut' : 'Prix bas'} atteint
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-mono font-bold text-[#191D1E]">{alert.trigger_price?.toFixed(2)} EUR</p>
                      <p className="text-xs text-[#687175]">
                        Seuil : {alert.threshold?.toFixed(2)} EUR
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => setPreviewLogId(alert.id)}
                        className="bg-[#397DFF] hover:bg-[#397DFF]/90 text-white"
                        data-testid={`preview-alert-${alert.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        Personnaliser & envoyer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDismiss(alert.id)}
                        className="text-[#687175] hover:text-red-500 border-[#BFC6CC]/30"
                        data-testid={`dismiss-alert-${alert.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={!!previewLogId}
        onOpenChange={(open) => { if (!open) setPreviewLogId(null); }}
        logId={previewLogId}
        onSent={handleSent}
      />
    </div>
  );
};

export default PendingAlertsPage;
